'use client';

import { cva } from 'class-variance-authority';
import React, { type JSX, useId, useState } from 'react';

import { Button, IconButton, Text } from '@videoblocks/design-system';
import { Close } from '@videoblocks/design-system/icons';

import type { ImageFormat } from './buildImageFormats';

interface ImageDownloadPopoverProps {
    formats: ImageFormat[];
    isDownloading: boolean;
    onSelectFormat: (formatName: string) => void;
    onClose: () => void;
}

// Colors follow VideoDownloadPopover: white outline and text, a white fill with dark text on
// hover, and a plain 50% fade (not the design-system grey fill) while a download is in flight,
// with the chosen format staying filled.
const formatButtonVariants = cva('min-w-16 flex-col gap-1 rounded-md border px-2 py-2', {
    variants: {
        isDownloading: {
            false: 'hover:border-white hover:text-gray-900',
            true: 'border-white bg-transparent text-white opacity-50 hover:border-white hover:bg-transparent hover:text-white',
        },
        isSelected: { true: '', false: '' },
    },
    compoundVariants: [
        {
            isDownloading: true,
            isSelected: true,
            class: 'bg-white text-gray-900 hover:bg-white hover:text-gray-900',
        },
    ],
});

// The popover sits inside the card's link area, so clicks must not bubble into a clickthrough.
function stopEvent(e: React.SyntheticEvent): void {
    e.preventDefault();
    e.stopPropagation();
}

export function ImageDownloadPopover({
    formats,
    isDownloading,
    onSelectFormat,
    onClose,
}: ImageDownloadPopoverProps): JSX.Element {
    const titleId = useId();
    const [selectedFormatName, setSelectedFormatName] = useState<string | null>(null);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-busy={isDownloading}
            className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-gray-900/60 px-4"
            onClick={stopEvent}
            onKeyDown={(e): void => {
                if (e.key === 'Escape') {
                    stopEvent(e);
                    onClose();
                }
            }}
        >
            <IconButton
                variant="transparent"
                aria-label="Close download options"
                className="absolute right-2 top-2 text-white"
                icon={<Close className="size-4 [&_path]:fill-current" />}
                onClick={(e): void => {
                    stopEvent(e);
                    onClose();
                }}
            />

            <div className="flex flex-col items-center gap-3">
                <Text asChild fontSize="base" fontWeight="regular" className="m-0 text-white">
                    <h2 id={titleId}>{'Download'}</h2>
                </Text>

                {formats.length === 0 ? (
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-400">
                        No formats available
                    </Text>
                ) : (
                    <div className="flex flex-wrap justify-center gap-1">
                        {formats.map((format, index) => (
                            <Button
                                key={format.formatName}
                                variant="outlined-light"
                                disabled={isDownloading}
                                autoFocus={index === 0}
                                aria-label={`Download ${format.formatName}${format.fileSize ? `, ${format.fileSize}` : ''}`}
                                buttonClassName={formatButtonVariants({
                                    isDownloading,
                                    isSelected: format.formatName === selectedFormatName,
                                })}
                                onClick={(e): void => {
                                    stopEvent(e);
                                    setSelectedFormatName(format.formatName);
                                    onSelectFormat(format.formatName);
                                }}
                            >
                                <Text asChild fontSize="2xl" fontWeight="bold" className="block">
                                    <span>{format.formatName}</span>
                                </Text>
                                {format.fileSize && (
                                    <Text asChild fontSize="xs" fontWeight="regular" className="block">
                                        <span>{format.fileSize}</span>
                                    </Text>
                                )}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
